import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, OnDestroy, inject, input, output } from '@angular/core';

export interface ResizeMovimento { largura: number; vaiFechar: boolean }
export interface ResizeFim { largura: number; fechar: boolean }

const PASSO_TECLADO_PX = 16;
/** Classe no <body> enquanto arrasta: cursor col-resize + sem seleção de texto (styles/layout.css). */
export const ONE_RESIZING_CLASS = 'one-resizing';

/**
 * Pega de redimensionamento horizontal (pointer capture). O host é a borda arrastável; a
 * diretiva só calcula e emite larguras clampadas — quem aplica é o consumidor. `fechaAlem` > 0
 * sinaliza intenção de FECHAR quando o ponteiro passa esse tanto além do mínimo (gaveta).
 */
@Directive({
  selector: '[oneResize]',
  standalone: true,
  host: {
    role: 'separator',
    'aria-orientation': 'vertical',
    tabindex: '0',
    '[attr.aria-valuemin]': 'min()',
    '[attr.aria-valuemax]': 'max()',
    '[attr.aria-valuenow]': 'larguraAtual()',
    '(pointerdown)': 'aoPressionar($event)',
    '(pointermove)': 'aoMover($event)',
    '(pointerup)': 'aoSoltar($event)',
    '(pointercancel)': 'aoCancelarPonteiro()',
    '(keydown)': 'aoTecla($event)',
  },
})
export class OneResizeDirective implements OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly doc = inject(DOCUMENT);

  edge = input<'left' | 'right'>('left');
  min = input.required<number>();
  max = input.required<number>();
  larguraAtual = input.required<number>();
  fechaAlem = input<number>(0);

  inicio = output<void>();
  movimento = output<ResizeMovimento>();
  fim = output<ResizeFim>();
  cancelado = output<void>();

  private x0: number | null = null;
  private larguraBase = 0;
  private ultimo: ResizeMovimento | null = null;
  private readonly aoEscape = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || this.x0 === null) return;
    this.encerrar();
    this.cancelado.emit();
  };

  aoPressionar(e: PointerEvent): void {
    if (e.button !== 0) return;
    e.preventDefault();
    this.x0 = e.clientX;
    this.larguraBase = this.larguraAtual();
    this.ultimo = { largura: this.clamp(this.larguraBase), vaiFechar: false };
    this.el.nativeElement.setPointerCapture?.(e.pointerId);
    this.doc.body.classList.add(ONE_RESIZING_CLASS);
    this.doc.addEventListener('keydown', this.aoEscape);
    this.inicio.emit();
  }

  aoMover(e: PointerEvent): void {
    if (this.x0 === null) return;
    const delta = this.edge() === 'left' ? this.x0 - e.clientX : e.clientX - this.x0;
    const bruta = this.larguraBase + delta;
    const vaiFechar = this.fechaAlem() > 0 && bruta < this.min() - this.fechaAlem();
    this.ultimo = { largura: this.clamp(bruta), vaiFechar };
    this.movimento.emit(this.ultimo);
  }

  aoSoltar(e: PointerEvent): void {
    if (this.x0 === null) return;
    this.el.nativeElement.releasePointerCapture?.(e.pointerId);
    const u = this.ultimo!;
    this.encerrar();
    this.fim.emit({ largura: u.largura, fechar: u.vaiFechar });
  }

  aoCancelarPonteiro(): void {
    if (this.x0 === null) return;
    const u = this.ultimo!;
    this.encerrar();
    this.fim.emit({ largura: u.largura, fechar: false });
  }

  aoTecla(e: KeyboardEvent): void {
    const atual = this.larguraAtual();
    const cresce = this.edge() === 'left' ? 'ArrowLeft' : 'ArrowRight';
    const encolhe = this.edge() === 'left' ? 'ArrowRight' : 'ArrowLeft';
    let nova: number | null = null;
    if (e.key === cresce) nova = atual + PASSO_TECLADO_PX;
    else if (e.key === encolhe) nova = atual - PASSO_TECLADO_PX;
    else if (e.key === 'Home') nova = this.max();
    else if (e.key === 'End') nova = this.min();
    if (nova === null) return;
    e.preventDefault();
    this.fim.emit({ largura: this.clamp(nova), fechar: false });
  }

  /** I2 (review final): desmontar (ex.: `@if` sai) no meio de um arrasto deixava `one-resizing`
   *  preso no <body> e o consumidor sem saber que o arrasto acabou — ninguém mais soltaria o
   *  ponteiro. `emit()` aqui roda enquanto a view ainda está sendo destruída, mas ANTES do
   *  Angular remover a subscription do binding de template (`executeOnDestroys` roda antes de
   *  `processCleanups` no motor de views) — o handler do pai ainda recebe o evento. */
  ngOnDestroy(): void {
    if (this.x0 === null) return;
    this.encerrar();
    this.cancelado.emit();
  }

  private clamp(v: number): number { return Math.min(this.max(), Math.max(this.min(), Math.round(v))); }

  private encerrar(): void {
    this.x0 = null;
    this.doc.body.classList.remove(ONE_RESIZING_CLASS);
    this.doc.removeEventListener('keydown', this.aoEscape);
  }
}
